import { useState, useEffect } from "react";
import {
  Settings, LogOut, Download, RefreshCw, Lightbulb,
  Trash2, BookOpen, Database, Flame, Eye, Clock, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";

import { MetricCards } from "../components/admin/MetricCards";
import { SessionsTable } from "../components/admin/SessionsTable";
import { SuggestionsPanel } from "../components/admin/SuggestionsPanel";
import { SettingsModal, ResetModal } from "../components/admin/Modals";
import { UserManagement } from "../components/admin/UserManagement";
import { AuditLog } from "../components/admin/AuditLog";
import { SurveyPanel } from "../components/admin/SurveyPanel";
import { SessionInsightsPanel } from "../components/admin/SessionInsightsPanel";
import { SecurityIncidentsPanel } from "../components/admin/SecurityIncidentsPanel";
import { ChangePasswordModal } from "../components/admin/ChangePasswordModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
  secondaryLight: "#f1f2f2",
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_role", response.data.role);
      onLogin(response.data.access_token, response.data.role, response.data.must_change_password);
      toast.success("Welcome back!");
    } catch (error) {
      const detail = error.response?.data?.detail || "Authentication failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: COLORS.secondaryLight }}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/moneyguro-logo.png" alt="Money Guro" className="mx-auto w-16 h-16 object-contain mb-4" data-testid="admin-login-logo" />
          <CardTitle className="text-2xl font-heading">
            <span style={{ color: COLORS.primaryDark }}>Money Guro</span>{" "}
            <span style={{ color: COLORS.primaryRed }}>Admin</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2" required data-testid="admin-username-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2" required data-testid="admin-password-input" />
            </div>
            <Button type="submit" className="w-full text-white" style={{ backgroundColor: COLORS.primaryDark }} disabled={loading} data-testid="admin-submit-btn">
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
          <p className="text-center mt-4 text-xs text-[#999]">Contact your Super Admin for account access</p>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Dashboard
const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [role, setRole] = useState(localStorage.getItem("admin_role") || "admin");
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const [forcePwChange, setForcePwChange] = useState(false);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [period, setPeriod] = useState("7d");
  const [costConfig, setCostConfig] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsGrouped, setSuggestionsGrouped] = useState({});
  const [pendingSuggestionsCount, setPendingSuggestionsCount] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState("pending");
  const [approvingId, setApprovingId] = useState(null);
  const [reassignCategory, setReassignCategory] = useState({});

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token) { fetchAnalytics(); fetchSessions(); fetchCostConfig(); fetchSuggestions(); fetchRole(); }
  }, [token, period]);

  const fetchRole = async () => {
    try {
      const res = await axios.get(`${API}/admin/me`, { headers: authHeaders });
      const r = res.data.role || "admin";
      setRole(r);
      localStorage.setItem("admin_role", r);
    } catch {}
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics?period=${period}`, { headers: authHeaders });
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async (page = 0, filters = {}) => {
    try {
      const params = new URLSearchParams({ limit: "50", offset: String(page * 50) });
      if (filters.search) params.set("search", filters.search);
      if (filters.sourceFilter) params.set("source", filters.sourceFilter);
      if (filters.startDate) params.set("start_date", filters.startDate);
      if (filters.endDate) params.set("end_date", filters.endDate);
      const response = await axios.get(`${API}/admin/sessions?${params}`, { headers: authHeaders });
      setSessions(response.data.sessions);
      setSessionsTotal(response.data.total || response.data.sessions.length);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  const fetchCostConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/cost-config`, { headers: authHeaders });
      setCostConfig(response.data);
    } catch {}
  };

  const fetchSuggestions = async (filter = suggestionFilter) => {
    try {
      const response = await axios.get(`${API}/admin/faq-suggestions?filter_status=${filter}`, { headers: authHeaders });
      setSuggestions(response.data.suggestions);
      setSuggestionsGrouped(response.data.grouped);
      setPendingSuggestionsCount(response.data.pending_count);
    } catch {}
  };

  const removeFromSuggestions = (userMessage) => {
    setSuggestions(prev => prev.filter(s => s.user_message !== userMessage));
    setSuggestionsGrouped(prev => {
      const updated = {};
      for (const [cat, items] of Object.entries(prev)) {
        const filtered = items.filter(s => s.user_message !== userMessage);
        if (filtered.length > 0) updated[cat] = filtered;
      }
      return updated;
    });
    setPendingSuggestionsCount(prev => Math.max(0, prev - 1));
  };

  const handleApproveSuggestion = async (suggestion, category = null) => {
    const targetCategory = category || reassignCategory[suggestion.user_message] || suggestion.nearest_category;
    setApprovingId(suggestion.user_message);
    try {
      await axios.post(`${API}/admin/faq-suggestions/approve`, { user_message: suggestion.user_message, category: targetCategory, add_as: "phrase" }, { headers: authHeaders });
      toast.success(`Added to ${targetCategory} FAQ!`);
      removeFromSuggestions(suggestion.user_message);
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setApprovingId(null);
    }
  };

  const handleDismissSuggestion = async (userMessage) => {
    try {
      await axios.post(`${API}/admin/faq-suggestions/dismiss`, { user_message: userMessage }, { headers: authHeaders });
      toast.success("Suggestion dismissed");
      removeFromSuggestions(userMessage);
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  const handleDismissAll = async (category, items) => {
    const pending = items.filter(s => s.status === "pending");
    try {
      await Promise.all(pending.map(s => axios.post(`${API}/admin/faq-suggestions/dismiss`, { user_message: s.user_message }, { headers: authHeaders })));
      pending.forEach(s => removeFromSuggestions(s.user_message));
      toast.success(`Dismissed all ${pending.length} suggestions in ${category.replace(/_/g, " ")}`);
    } catch {
      toast.error("Some dismissals failed");
      fetchSuggestions();
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/admin/session/${sessionId}`, { headers: authHeaders });
      setSessionMessages(response.data.messages);
      setSelectedSession(sessionId);
    } catch {
      toast.error("Failed to load session details");
    }
  };

  const updateCostConfig = async (newConfig) => {
    try {
      await axios.put(`${API}/admin/cost-config`, newConfig, { headers: authHeaders });
      setCostConfig(newConfig);
      toast.success("Cost configuration updated!");
      setShowSettings(false);
    } catch {
      toast.error("Failed to update config");
    }
  };

  const exportData = async (format = "csv") => {
    try {
      const response = await axios.get(`${API}/admin/export?period=${period}&format=${format}`, { headers: authHeaders });
      if (format === "csv") {
        const blob = new Blob([response.data.csv_data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = response.data.filename; a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `money_guro_analytics_${period}.json`; a.click();
      }
      toast.success("Data exported successfully!");
    } catch {
      toast.error("Failed to export data");
    }
  };

  const handleLogout = () => { localStorage.removeItem("admin_token"); localStorage.removeItem("admin_role"); setToken(null); setRole("admin"); };

  const handleResetAnalytics = async () => {
    if (resetPhrase !== "RESET ALL DATA") { toast.error("Please type 'RESET ALL DATA' exactly to confirm."); return; }
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/admin/reset-analytics`, { confirmation_phrase: resetPhrase }, { headers: authHeaders });
      toast.success(response.data.message);
      setShowResetModal(false);
      setResetPhrase("");
      fetchAnalytics();
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed");
    } finally {
      setResetLoading(false);
    }
  };

  if (!token) return <AdminLogin onLogin={(t, r, mustChange) => { setToken(t); setRole(r || "admin"); if (mustChange) { setForcePwChange(true); setShowChangePw(true); } }} />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.secondaryLight }}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: COLORS.primaryDark, borderTopColor: 'transparent' }}></div>
          <p className="text-[#666]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.secondaryLight }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/moneyguro-logo.png" alt="Money Guro" className="w-10 h-10 object-contain" data-testid="admin-dashboard-logo" />
            <div>
              <h1 className="font-heading font-bold">
                <span style={{ color: COLORS.primaryDark }}>Money Guro</span>{" "}
                <span style={{ color: COLORS.primaryRed }}>Admin</span>
              </h1>
              <p className="text-xs text-[#666]">
                Analytics Dashboard
                {role === "super_admin" && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">Super Admin</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { fetchAnalytics(); fetchSessions(); fetchSuggestions(); toast.success("Data refreshed!"); }} data-testid="refresh-btn"><RefreshCw size={18} /></Button>
            {pendingSuggestionsCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => { setShowSuggestions(true); setTimeout(() => document.getElementById('faq-suggestions-panel')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="relative" data-testid="suggestions-nav-btn">
                <Lightbulb size={18} className="text-amber-500" />
                <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{pendingSuggestionsCount}</span>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} data-testid="settings-btn"><Settings size={18} /></Button>
            <Button variant="ghost" size="sm" onClick={() => { setForcePwChange(false); setShowChangePw(true); }} data-testid="change-pw-btn" title="Change Password"><Lock size={18} /></Button>
            <Button variant="ghost" size="sm" onClick={() => setShowResetModal(true)} data-testid="reset-stats-btn" className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 size={18} /></Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"><LogOut size={18} /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Period Selector + Export */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex gap-2">
            {["today", "7d", "30d", "all"].map((p) => (
              <Button key={p} variant={period === p ? "default" : "outline"} size="sm" onClick={() => setPeriod(p)} style={period === p ? { backgroundColor: COLORS.primaryDark } : {}} data-testid={`period-${p}-btn`}>
                {p === "today" ? "Today" : p === "7d" ? "7 Days" : p === "30d" ? "30 Days" : "All Time"}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => exportData("csv")} data-testid="export-csv-btn"><Download size={16} className="mr-1" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={() => exportData("json")} data-testid="export-json-btn"><Download size={16} className="mr-1" /> JSON</Button>
          </div>
        </div>

        {analytics && (
          <>
            <MetricCards analytics={analytics} />

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><BookOpen size={18} /> Response Source</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[{ name: `FAQ (${analytics.faq_hits})`, value: analytics.faq_hits }, { name: `LLM (${analytics.llm_calls})`, value: analytics.llm_calls }]} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                          <Cell fill="#22c55e" /><Cell fill={COLORS.primaryRed} />
                        </Pie>
                        <Tooltip /><Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Database size={18} /> FAQ Categories Hit</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-64 overflow-y-auto">
                    {analytics.faq_breakdown?.length > 0 ? (
                      <div className="space-y-2">
                        {analytics.faq_breakdown.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                            <span className="font-medium capitalize">{item.category.replace(/_/g, ' ')}</span>
                            <span className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-sm font-bold">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">No FAQ data yet</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Popular Topics</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.popular_topics} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis dataKey="topic" type="category" width={70} tick={{ fontSize: 11 }} /><Tooltip />
                        <Bar dataKey="count" fill={COLORS.primaryDark} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Stats */}
            <Card className="mb-6">
              <CardHeader><CardTitle className="text-lg">Daily Activity (FAQ vs LLM)</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.daily_stats}>
                      <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" tick={{ fontSize: 12 }} /><YAxis /><Tooltip /><Legend />
                      <Bar dataKey="faq" name="FAQ (Free)" fill="#22c55e" stackId="a" />
                      <Bar dataKey="llm" name="LLM (Paid)" fill={COLORS.primaryRed} stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Heatmap */}
            {analytics.heatmap?.length > 0 && (
              <Card className="mb-6" data-testid="heatmap-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Flame size={18} style={{ color: COLORS.primaryRed }} /> Conversation Heatmap
                    <span className="text-xs font-normal text-[#999] ml-2">Peak usage by day & hour (UTC)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                    const maxCount = Math.max(...analytics.heatmap.map(h => h.count), 1);
                    const getColor = (count) => {
                      if (count === 0) return "#f3f4f6";
                      const intensity = Math.min(count / maxCount, 1);
                      if (intensity < 0.25) return "#dcfce7";
                      if (intensity < 0.5) return "#86efac";
                      if (intensity < 0.75) return "#f97316";
                      return "#ec474e";
                    };
                    return (
                      <div className="overflow-x-auto">
                        <div className="min-w-[700px]">
                          <div className="flex mb-1">
                            <div className="w-10 flex-shrink-0" />
                            {Array.from({ length: 24 }, (_, i) => (<div key={i} className="flex-1 text-center text-[10px] text-[#999]">{i % 3 === 0 ? `${i}h` : ""}</div>))}
                          </div>
                          {days.map((day) => (
                            <div key={day} className="flex items-center mb-[2px]">
                              <div className="w-10 flex-shrink-0 text-xs font-medium text-[#666] text-right pr-2">{day}</div>
                              {Array.from({ length: 24 }, (_, hour) => {
                                const cell = analytics.heatmap.find(h => h.day === day && h.hour === hour);
                                const count = cell?.count || 0;
                                return (<div key={hour} className="flex-1 mx-[1px] rounded-sm cursor-default transition-transform hover:scale-110" style={{ backgroundColor: getColor(count), height: 28, minWidth: 12 }} title={`${day} ${hour}:00 - ${count} message${count !== 1 ? "s" : ""}`} />);
                              })}
                            </div>
                          ))}
                          <div className="flex items-center justify-end gap-2 mt-3 text-xs text-[#666]">
                            <span>Less</span>
                            {["#f3f4f6", "#dcfce7", "#86efac", "#f97316", "#ec474e"].map((color, i) => (<div key={i} className="w-4 h-4 rounded-sm" style={{ backgroundColor: color }} />))}
                            <span>More</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </>
        )}

        <SuggestionsPanel
          suggestions={suggestions} suggestionsGrouped={suggestionsGrouped}
          pendingSuggestionsCount={pendingSuggestionsCount} showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions} suggestionFilter={suggestionFilter}
          onFilterChange={(v) => { setSuggestionFilter(v); fetchSuggestions(v); }}
          onApprove={handleApproveSuggestion} onDismiss={handleDismissSuggestion}
          onDismissAll={handleDismissAll} approvingId={approvingId}
          reassignCategory={reassignCategory} setReassignCategory={setReassignCategory}
          learnedKeywords={analytics?.learned_keywords || 0}
        />

        {role === "super_admin" && <UserManagement token={token} />}

        {role === "super_admin" && <AuditLog token={token} />}

        {role === "super_admin" && <SecurityIncidentsPanel token={token} />}

        <SurveyPanel token={token} surveyOverview={analytics?.survey} />

        <SessionInsightsPanel token={token} />

        <SessionsTable sessions={sessions} sessionsTotal={sessionsTotal} onViewSession={fetchSessionDetails} onFetchSessions={fetchSessions} />

        {/* Session Detail Modal */}
        {selectedSession && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Session: {selectedSession.slice(0, 8)}...</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedSession(null)}>&times;</Button>
              </CardHeader>
              <CardContent className="overflow-y-auto max-h-[60vh]">
                <div className="space-y-4">
                  {sessionMessages.map((msg, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${msg.source === "faq" ? "bg-green-50" : "bg-gray-50"}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${msg.source === "faq" ? "bg-green-200 text-green-800" : "text-white"}`} style={msg.source !== "faq" ? { backgroundColor: COLORS.primaryRed } : {}}>
                          {msg.source?.toUpperCase() || "LLM"}
                        </span>
                        <span className="text-xs text-[#666]">{msg.timestamp?.slice(0, 19)}</span>
                        {msg.cost > 0 && <span className="text-xs" style={{ color: COLORS.primaryRed }}>${msg.cost.toFixed(4)}</span>}
                      </div>
                      <p className="text-sm font-medium mb-1">User: {msg.user_message}</p>
                      <p className="text-sm text-[#666] whitespace-pre-wrap">{msg.assistant_response}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {showSettings && costConfig && <SettingsModal costConfig={costConfig} analytics={analytics} onSave={updateCostConfig} onClose={() => setShowSettings(false)} />}
        {showResetModal && <ResetModal resetPhrase={resetPhrase} setResetPhrase={setResetPhrase} onReset={handleResetAnalytics} onClose={() => { setShowResetModal(false); setResetPhrase(""); }} loading={resetLoading} />}
        <ChangePasswordModal
          open={showChangePw}
          token={token}
          forced={forcePwChange}
          onClose={(success) => { setShowChangePw(false); setForcePwChange(false); if (success) toast.success("Password updated!"); }}
        />
      </main>
    </div>
  );
};

export default AdminDashboard;
